// Persistent task hook: survives page navigation and refresh.
// Backs a single long-running /api/jobs task with localStorage.
//
// How it works:
//  1. startJob() POSTs to /api/jobs, gets a jobId, writes it to localStorage.
//  2. While jobId exists, the hook polls /api/jobs/:id for status every 2s
//     and tails /api/jobs/:id/stream via SSE for log output.
//  3. When status becomes `done` / `failed`, the hook auto-clears localStorage
//     (but keeps jobId in memory so current view still shows the result).
//  4. If the user leaves the page and comes back while the task is still
//     running, the hook reads localStorage on mount and reattaches.
//  5. Optional `reattach` config makes step (4) work across devices: on
//     mount, if localStorage has no jobId, we ask the server for the
//     current user's in-flight jobs of cmd `reattach.cmd`, run the
//     optional `reattach.match` predicate, and adopt the newest match
//     as our jobId. Without this, device B never sees that device A
//     started a long-running scan/tailor/research job.
//
// Storage key format: `nice2job.job.<pageKey>` (one active job per key).

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPost, ApiClientError } from './api';
import { useSSE } from './sse';
import { friendlyError } from './errorMessages';
import { useZh } from './useProduct';

type BackendStatus = 'pending' | 'running' | 'done' | 'failed';
export type JobRunnerStatus = 'idle' | BackendStatus;

interface JobRecord {
  id: string;
  cmd: string;
  payload: string;
  status: BackendStatus;
  created: number;
  updated: number;
  code: number | null;
  pid: number | null;
  userId?: number | string | null;
}

export interface JobReattachConfig {
  // Server-side cmd to look for. Required when reattach is configured.
  cmd: string;
  // Optional payload predicate. The hook parses each candidate job's
  // `payload` as JSON (falling back to the raw string under `_raw`) and
  // passes the parsed object plus the original record. Return true to
  // adopt that job. Most callers use this to narrow by url / company.
  match?: (parsedPayload: Record<string, unknown> & { _raw?: string }, job: JobRecord) => boolean;
}

export interface UseJobRunnerOptions {
  reattach?: JobReattachConfig;
}

export interface UseJobRunnerResult {
  jobId: string | null;
  status: JobRunnerStatus;
  log: string;                // accumulated stdout + stderr text
  exitCode: number | null;
  errorLine: string | null;
  isRunning: boolean;         // pending or running
  isActive: boolean;          // jobId is set (could be running or finished-this-session)
  startJob: (cmd: string, payload?: unknown) => Promise<string | null>;
  startError: string | null;  // why the last startJob refused, user-readable
  clearStartError: () => void;
  cancel: () => Promise<void>; // DELETE /api/jobs/:id — kills child + clears state
  dismiss: () => void;
}

const KEY_PREFIX = 'nice2job.job.';

export function useJobRunner(pageKey: string, options?: UseJobRunnerOptions): UseJobRunnerResult {
  const storageKey = KEY_PREFIX + pageKey;
  const [jobId, setJobId] = useState<string | null>(() => {
    try { return localStorage.getItem(storageKey); } catch { return null; }
  });
  const [startError, setStartError] = useState<unknown>(null);
  const zh = useZh();

  // Cross-device reattach: on mount, if we don't already have a jobId for
  // this page, ask the server "are any of MY in-flight jobs of cmd X a
  // match for this view?" and adopt the newest match. Runs at most once
  // per hook mount — we gate on a ref to avoid re-firing on rerender.
  // No-op when `options.reattach` is omitted.
  const reattach = options?.reattach;
  useEffect(() => {
    if (!reattach) return;
    if (jobId) return; // already attached (this device or just adopted)
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet<{ jobs: JobRecord[] }>(
          `/api/jobs?cmd=${encodeURIComponent(reattach.cmd)}&status=pending,running`
        );
        if (cancelled) return;
        const jobs = Array.isArray(res?.jobs) ? res.jobs : [];
        if (jobs.length === 0) return;
        const predicate = reattach.match;
        const matched = jobs.find((j) => {
          if (!predicate) return true;
          let parsed: Record<string, unknown> & { _raw?: string };
          try {
            const obj = j.payload ? JSON.parse(j.payload) : {};
            parsed = (obj && typeof obj === 'object') ? obj as Record<string, unknown> : { _raw: j.payload };
          } catch {
            parsed = { _raw: j.payload };
          }
          try { return !!predicate(parsed, j); } catch { return false; }
        });
        if (!matched) return;
        try { localStorage.setItem(storageKey, matched.id); } catch { /* noop */ }
        setJobId(matched.id);
      } catch { /* offline / 401 — keep idle */ }
    })();
    return () => { cancelled = true; };
    // We intentionally only run reattach on mount + when jobId clears.
    // `reattach.cmd` is part of the static config so we don't depend on
    // its identity (callers might inline the object literal).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, storageKey]);

  // Poll job status (lightweight; stops when terminal)
  const statusQ = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => apiGet<JobRecord>(`/api/jobs/${jobId}`),
    enabled: !!jobId,
    retry: 0,
    staleTime: 0,
    refetchInterval: (q) => {
      const s = (q.state.data as JobRecord | undefined)?.status;
      return s === 'pending' || s === 'running' ? 2000 : false;
    },
  });

  // Drop stale localStorage ids (job file was deleted or never existed)
  useEffect(() => {
    if (!jobId) return;
    if (statusQ.isError && statusQ.error instanceof ApiClientError && statusQ.error.status === 404) {
      try { localStorage.removeItem(storageKey); } catch { /* noop */ }
      setJobId(null);
    }
  }, [jobId, statusQ.isError, statusQ.error, storageKey]);

  const status: JobRunnerStatus =
    statusQ.data?.status ?? (jobId ? 'pending' : 'idle');

  // Keep the jobId in localStorage even after completion so the user can
  // return to the view later and still see the final log + parsed summary
  // (ATS score delta, missing keywords, etc). `dismiss()` explicitly wipes.

  // SSE stream only while the job can still produce output
  const streamUrl = jobId && (status === 'pending' || status === 'running')
    ? `/api/jobs/${jobId}/stream`
    : null;
  const sse = useSSE(streamUrl, { enabled: !!streamUrl });

  // Even after the stream ends, we may need to fetch the final log on resume
  // (status was already 'done' when we mounted, so we never opened SSE).
  const [fallbackLog, setFallbackLog] = useState('');
  useEffect(() => {
    // Always reset on jobId change — including switching from one job to
    // another, not just to null. Otherwise the new job briefly shows the
    // previous job's tail through fallbackLog before its own SSE catches up.
    setFallbackLog('');
    if (!jobId) return;
    if (status !== 'done' && status !== 'failed') return;
    // Stream is closed, fetch the full log by making one last SSE request
    // The SSE endpoint replays and then closes immediately for done jobs.
    const es = new EventSource(`/api/jobs/${jobId}/stream`);
    let acc = '';
    es.onmessage = (msg) => {
      try {
        const obj = JSON.parse(msg.data) as { type?: string; text?: string };
        if ((obj.type === 'stdout' || obj.type === 'stderr') && obj.text) {
          acc += obj.text;
        }
      } catch { /* noop */ }
    };
    es.onerror = () => {
      es.close();
      setFallbackLog(acc);
    };
    return () => es.close();
  }, [jobId, status]);

  const startJob = async (cmd: string, payload?: unknown): Promise<string | null> => {
    const payloadStr = payload == null ? ''
      : typeof payload === 'string' ? payload
      : JSON.stringify(payload);
    try {
      const res = await apiPost<{ job_id: string; status: string }>('/api/jobs', {
        cmd, payload: payloadStr,
      });
      try { localStorage.setItem(storageKey, res.job_id); } catch { /* noop */ }
      setStartError(null);
      setJobId(res.job_id);
      return res.job_id;
    } catch (err) {
      // Swallowing this made a full queue or an exhausted daily allowance look
      // like a dead button — the click did nothing and said nothing. Keep the
      // string|null return (every existing caller depends on it) and surface
      // the reason separately.
      setStartError(err);
      return null;
    }
  };

  const dismiss = () => {
    try { localStorage.removeItem(storageKey); } catch { /* noop */ }
    setJobId(null);
  };

  // Kill a running job via DELETE /api/jobs/:id. Server sends SIGTERM to the
  // child and unlinks the job/log files, so the next poll 404s and the hook
  // clears itself. We also proactively null out local state for immediate UX.
  const cancel = async (): Promise<void> => {
    if (!jobId) return;
    try {
      await fetch(`/api/jobs/${jobId}`, { method: 'DELETE' });
    } catch { /* fire-and-forget; clearing local state still gives visual feedback */ }
    try { localStorage.removeItem(storageKey); } catch { /* noop */ }
    setJobId(null);
  };

  const log = sse.tokens || fallbackLog;
  const isRunning = status === 'pending' || status === 'running';

  return {
    jobId,
    status,
    log,
    exitCode: statusQ.data?.code ?? null,
    // Suppress "connection lost" when the job actually finished normally.
    errorLine: status === 'failed' ? sse.error : null,
    isRunning,
    isActive: !!jobId,
    startJob,
    // Why the last startJob() refused, already translated. null when the last
    // attempt succeeded or none has been made.
    startError: startError ? friendlyError(startError, zh) : null,
    clearStartError: () => setStartError(null),
    cancel,
    dismiss,
  };
}
